import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getTripHistory } from '../../services/tripService';
import { getRiderTaxInvoice, downloadFromSignedUrl } from '../../services/complianceService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function TripHistory() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);  // bookingId being downloaded


  useEffect(() => {
    if (!userProfile) return;
    setLoading(true);
    getTripHistory(userProfile.id, userProfile.role).then((data) => {
      setTrips(data);
      setLoading(false);
    });
  }, [userProfile]);


const handleDownloadInvoice = async (e, tripId, referenceNumber) => {
    e.stopPropagation();  // don't trigger card navigation
    setDownloadingInvoice(tripId);
    try {
      const result = await getRiderTaxInvoice(tripId);
      downloadFromSignedUrl(result.url, `reservo-invoice-${referenceNumber || tripId}.pdf`);
      toast.success('Tax invoice downloaded');
    } catch (err) {
      toast.error(err.message || 'Could not download invoice');
    }
    setDownloadingInvoice(null);
  };

  const filtered = filter === 'all' ? trips : trips.filter((t) => {
    if (filter === 'upcoming') return ['pending_fare_calc', 'pending_pool', 'pending_targeted', 'pending', 'confirmed', 'driverAssigned', 'en_route', 'arrived', 'pin_verified', 'in_progress'].includes(t.status);
    return t.status === filter;
  });

  const statusBadge = (status, trip) => {
    const colors = {
      pending_fare_calc: '#fbbf24', pending_pool: '#fbbf24', pending_targeted: '#fbbf24',
      pending: '#fbbf24', confirmed: '#6366f1', driverAssigned: '#6366f1',
      en_route: '#3b82f6', arrived: '#2dd4a8', pin_verified: '#2dd4a8',
      in_progress: '#2dd4a8', completed: '#5a6578', cancelled: '#ff6b6b',
    };
    const labels = {
      pending_fare_calc: 'Pricing…', pending_pool: 'Finding driver', pending_targeted: 'Offered to driver',
      pending: 'Pending', confirmed: 'Confirmed', driverAssigned: 'Driver Assigned',
      en_route: 'En Route', arrived: 'Arrived', pin_verified: 'PIN Verified',
      in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled',
    };
    const pickupTime = trip?.scheduledPickupTime?.toDate ? trip.scheduledPickupTime.toDate() : null;
    const isOverdue = pickupTime && pickupTime < new Date() && ['pending', 'confirmed', 'driverAssigned'].includes(status);
    if (isOverdue) {
      return (
        <span className="px-2.5 py-1 rounded-full text-[0.75rem] font-semibold" style={{ color: '#ff6b6b', background: '#ff6b6b15' }}>
          Overdue
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-[0.75rem] font-semibold" style={{ color: colors[status] || '#5a6578', background: `${colors[status] || '#94a3b8'}15` }}>
        {labels[status] || status}
      </span>
    );
  };


  return (
    <div className="max-w-[600px] mx-auto p-6">
      {/* Navigation */}
      <div className="flex justify-between items-center mb-5">
        <button onClick={() => navigate(`/${userProfile?.role || 'rider'}`)} className="bg-none border-0 cursor-pointer text-brand-primary font-semibold text-[0.9rem]">
          ← Home
        </button>
        <button onClick={() => navigate('/rider/book')} className="px-4 py-2 rounded-full border-[1.5px] border-brand-primary bg-white cursor-pointer font-semibold text-[0.85rem] text-brand-primary">
          + New Booking
        </button>
      </div>

      <h1 className="font-serif mt-0 mb-6 text-brand-dark">My Trips</h1>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {['upcoming', 'all', 'completed', 'cancelled'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className="px-4 py-2 rounded-full border-[1.5px] cursor-pointer font-semibold text-[0.85rem]"
            style={{
              borderColor: filter === f ? '#2dd4a8' : '#e2e8f0',
              background: filter === f ? 'rgba(45,212,168,0.06)' : '#fff',
              color: filter === f ? '#2dd4a8' : '#5a6578',
            }}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center text-muted">
          {filter === 'upcoming' ? 'No upcoming trips' : 'No trips found'}
        </div>
      ) : (
        filtered.map((trip) => (
          <div key={trip.id} onClick={() => navigate(`/${userProfile.role}/trip/${trip.id}`)} className="p-4 rounded-2xl border border-slate-200 mb-3 cursor-pointer bg-white"
            style={{ transition: 'box-shadow 0.2s' }}>
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-brand-dark">{trip.referenceNumber}</span>
              {statusBadge(trip.status, trip)}
            </div>
            <div className="text-[0.85rem] text-body mb-1">
              {trip.pickupAddress || trip.pickup?.address || 'Pickup'} → {trip.destinationAddress || trip.destination?.address || 'Destination'}
            </div>
            <div className="flex justify-between text-[0.8rem] text-muted">
              <span>{trip.scheduledPickupTime?.toDate ? new Date(trip.scheduledPickupTime.toDate()).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
              <span className="font-semibold text-brand-dark">${trip.fareBreakdown?.totalFare?.toFixed(2) || '—'}</span>
            </div>

            {/* Tax invoice download — riders only, completed trips only (Batch 10.5 M3) */}
            {userProfile?.role === 'rider' && trip.status === 'completed' && (
              <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex justify-end">
                <button
                  onClick={(e) => handleDownloadInvoice(e, trip.id, trip.referenceNumber)}
                  disabled={downloadingInvoice === trip.id}
                  className="px-3 py-1.5 rounded-lg border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer text-[0.78rem] font-semibold"
                  style={{ opacity: downloadingInvoice === trip.id ? 0.6 : 1 }}
                >
                  {downloadingInvoice === trip.id ? 'Generating...' : '📄 Tax invoice'}
                </button>
              </div>
            )}
          </div>
        ))
      )}
    <BackButtonBottom to={`/${userProfile?.role || 'rider'}`} label="Home" />
    </div>
  );
}